import type { ReactNode } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Heading,
  I,
  IconTile,
  Row,
  Stack,
  type IconName,
} from '@leanin/ui';
import { FlowShell, type FlowStep } from '@/components/onboarding/FlowShell';
import { Picture, type PictureSource } from '@/components/media/Picture';
import networksDirectory from '@/assets/onboarding/networks-directory.png?w=240;480;720&format=avif;webp;png&as=picture';
import groupsDirectory from '@/assets/onboarding/groups-directory.png?w=240;480;720&format=avif;webp;png&as=picture';
import memberDirectory from '@/assets/onboarding/member-directory.png?w=240;480;720&format=avif;webp;png&as=picture';

// Lean In Member onboarding tour — step 3 of 4.
// Three sibling pathways beyond Circles: local Networks, discussion
// groups, and the member Directory. Each section is a section heading
// + body description + an annotated screenshot.

const STEPS: ReadonlyArray<FlowStep> = [
  { label: 'Welcome' },
  { label: 'Lean In Circles' },
  { label: 'Get involved' },
  { label: 'Build leadership skills' },
  { label: "You're in" },
];

type Section = {
  readonly icon: IconName;
  readonly tone: 'burgundy' | 'orange' | 'emerald';
  readonly title: string;
  readonly description: ReactNode;
  readonly image: { readonly source: PictureSource; readonly alt: string };
};

// Icon + tone pairings mirror the CM "Connect with your community"
// cards (cm/connect.tsx) so the visual language stays consistent
// across persona tours — globe/burgundy for Networks, chat/orange for
// discussion groups, users/emerald for the member directory. Each
// section renders inside a Card; the screenshot lives where the CM
// version's TextLink CTA does.
const SECTIONS: ReadonlyArray<Section> = [
  {
    icon: 'globe',
    tone: 'burgundy',
    title: 'Find your local Lean In Network.',
    description: (
      <>
        Lean In Networks are volunteer-led communities in more than{' '}
        <strong style={{ color: 'var(--primary)' }}>
          100+ cities and 40 countries
        </strong>{' '}
        around the world. Each Network reflects its local community and is
        united by a shared mission: to empower women to lead. Join a Lean In
        Network to participate in local events, and find a community of women
        who get it.
      </>
    ),
    image: {
      source: networksDirectory,
      alt: 'Networks directory page with search, filters, and a grid of Network cards',
    },
  },
  {
    icon: 'chat',
    tone: 'orange',
    title: 'Join a discussion group.',
    description:
      'From AI to working parenthood to career pivots — find a topic that matters to you and jump into the conversation.',
    image: {
      source: groupsDirectory,
      alt: 'Discussion groups directory page with search and a grid of group cards',
    },
  },
  {
    icon: 'users',
    tone: 'emerald',
    title: 'Explore the member directory.',
    description:
      'Looking for a co-leader, a collaborator, or just someone who gets what you’re building? Search by location, industry, or shared interest and make the connection.',
    image: {
      source: memberDirectory,
      alt: 'Member directory page with search, filters, and a grid of member cards',
    },
  },
];

export const Route = createFileRoute('/onboarding/get-involved')({
  component: OnboardingGetInvolvedPage,
});

function OnboardingGetInvolvedPage() {
  const navigate = useNavigate();
  return (
    <FlowShell
      role="Lean In Member"
      stepIndex={2}
      steps={STEPS}
      minutes={2}
      title="Other ways to get involved in the community."
      primary={
        <Button
          variant="primary"
          iconRight={I.arrow}
          onClick={() => navigate({ to: '/onboarding/build-skills' })}
        >
          Next
        </Button>
      }
      secondary={
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/onboarding/circles' })}
        >
          Back
        </Button>
      }
    >
      <Stack gap="2xl">
        <Stack gap="md">
          <Heading level={2}>Connect with people who get it</Heading>
          <Body size="md">
            In addition to joining a small Lean In Circle, there are lots
            of other ways to connect with the global Lean In community
            through the Lean In Connect platform.
          </Body>
        </Stack>

        {SECTIONS.map((section) => (
          <Card key={section.title} pad="default">
            <Stack gap="md">
              <Row gap="md" align="center">
                <IconTile tone={section.tone}>{I[section.icon]}</IconTile>
                <Heading level={3}>{section.title}</Heading>
              </Row>
              <Body size="md">{section.description}</Body>
              <Card pad="flush" clip style={{ maxWidth: 480 }}>
                <Picture
                  image={section.image.source}
                  alt={section.image.alt}
                  sizes="(max-width: 480px) 100vw, 480px"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Card>
            </Stack>
          </Card>
        ))}
      </Stack>
    </FlowShell>
  );
}
